"use client";

import Link from "next/link";
import { ResponsiveLine } from "@nivo/line";
import type { IconType } from "react-icons";
import {
  MdAccessTime,
  MdApartment,
  MdArrowForward,
  MdBusiness,
  MdTrendingUp,
} from "react-icons/md";

import styles from "./market-overview.module.css";
import { useRef, useState, type CSSProperties } from "react";

type MarketOverviewProps = {
  isGlobal: boolean;
  marketName: string;
  guidesHref: string;
};

type MarketTrendPoint = {
  month: string;
  value: number;
  growth: number;
};

const globalTrend: MarketTrendPoint[] = [
  { month: "Jul", value: 182000, growth: 0.6 },
  { month: "Aug", value: 183400, growth: 0.8 },
  { month: "Sep", value: 184200, growth: 0.4 },
  { month: "Oct", value: 185900, growth: 0.9 },
  { month: "Nov", value: 186500, growth: 0.3 },
  { month: "Dec", value: 188200, growth: 0.9 },
  { month: "Jan", value: 189600, growth: 0.7 },
  { month: "Feb", value: 190400, growth: 0.4 },
  { month: "Mar", value: 191900, growth: 0.8 },
  { month: "Apr", value: 193100, growth: 0.6 },
  { month: "May", value: 194000, growth: 0.5 },
  { month: "Jun", value: 195000, growth: 0.5 },
];

const localTrend: MarketTrendPoint[] = [
  { month: "Jul", value: 1184, growth: 0.4 },
  { month: "Aug", value: 1191, growth: 0.6 },
  { month: "Sep", value: 1198, growth: 0.6 },
  { month: "Oct", value: 1202, growth: 0.3 },
  { month: "Nov", value: 1208, growth: 0.5 },
  { month: "Dec", value: 1215, growth: 0.6 },
  { month: "Jan", value: 1220, growth: 0.4 },
  { month: "Feb", value: 1224, growth: 0.3 },
  { month: "Mar", value: 1229, growth: 0.4 },
  { month: "Apr", value: 1234, growth: 0.4 },
  { month: "May", value: 1237, growth: 0.2 },
  { month: "Jun", value: 1240, growth: 0.2 },
];

function formatGrowth(value: number) {
  return `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function formatMarketValue(value: number, isGlobal: boolean) {
  const formatted = new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(value);

  return isGlobal ? `EUR ${formatted}` : `EUR ${formatted}/m2`;
}

function formatAxisValue(value: number, isGlobal: boolean) {
  if (isGlobal) {
    return `EUR ${new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 0,
    }).format(value)}`;
  }

  return `EUR ${value}`;
}

export default function MarketOverview({
  isGlobal,
  marketName,
  guidesHref,
}: MarketOverviewProps) {
  const trend = isGlobal ? globalTrend : localTrend;
  const latest = trend[trend.length - 1];
  const yearlyGrowth = ((latest.value - trend[0].value) / trend[0].value) * 100;

  const chartData = [
    {
      id: isGlobal ? "Average destination price" : "Pret mediu pe m2",
      data: trend.map((point) => ({
        x: point.month,
        y: point.value,
        growth: point.growth,
      })),
    },
  ];

  const metrics: Array<{
    label: string;
    value: string;
    trend: string;
    detail: string;
    Icon: IconType;
    isPositive: boolean;
  }> = [
    {
      label: isGlobal ? "Average entry price" : "Pret mediu",
      value: formatMarketValue(latest.value, isGlobal),
      trend: formatGrowth(yearlyGrowth),
      detail: isGlobal ? "annual movement" : "crestere anuala",
      Icon: MdApartment,
      isPositive: true,
    },
    {
      label: "Randament inchiriere",
      value: isGlobal ? "4.8%" : "5.2%",
      trend: "-0.3%",
      detail: "fata de luna trecuta",
      Icon: MdTrendingUp,
      isPositive: false,
    },
    {
      label: "Proprietati noi",
      value: isGlobal ? "12,840" : "2,843",
      trend: "+12.7%",
      detail: "inventory expansion",
      Icon: MdBusiness,
      isPositive: true,
    },
    {
      label: "Timp mediu de vanzare",
      value: isGlobal ? "58 zile" : "52 zile",
      trend: "-3 zile",
      detail: "market is moving faster",
      Icon: MdAccessTime,
      isPositive: true,
    },
  ];

  const [isMonthDragging, setIsMonthDragging] = useState(false);
  const [monthStartX, setMonthStartX] = useState(0);
  const [monthScrollLeft, setMonthScrollLeft] = useState(0);
  const monthStripRef = useRef<HTMLDivElement>(null);

  const handleMonthMouseDown = (e: React.MouseEvent) => {
    if (!monthStripRef.current) return;

    setIsMonthDragging(true);
    setMonthStartX(e.pageX - monthStripRef.current.offsetLeft);
    setMonthScrollLeft(monthStripRef.current.scrollLeft);
  };

  const handleMonthMouseLeave = () => {
    setIsMonthDragging(false);
  };

  const handleMonthMouseUp = () => {
    setIsMonthDragging(false);
  };

  const handleMonthMouseMove = (e: React.MouseEvent) => {
    if (!isMonthDragging || !monthStripRef.current) return;

    e.preventDefault();

    const x = e.pageX - monthStripRef.current.offsetLeft;
    const walk = (x - monthStartX) * 1.5;

    monthStripRef.current.scrollLeft = monthScrollLeft - walk;
  };

  const chartMargin = {
    top: 24,
    right: 24,
    bottom: 44,
    left: isGlobal ? 78 : 66,
  };

  const [activeChartTooltip, setActiveChartTooltip] = useState<{
    x: number;
    y: number;
    month: string;
    value: string;
    growth: number;
    isEndPoint: boolean;
  } | null>(null);

  const handleChartPointActivate = (point: any) => {
    const month = String(point.data.xFormatted ?? point.data.x);
    const pointIndex = trend.findIndex((item) => item.month === month);

    setActiveChartTooltip({
      x: chartMargin.left + point.x,
      y: chartMargin.top + point.y,
      month,
      value: String(point.data.yFormatted),
      growth: Number(point.data.growth ?? 0),
      isEndPoint:
        pointIndex === trend.length - 1 || pointIndex === trend.length - 2,
    });
  };

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <div className={styles.titleBlock}>
          <span className={styles.eyebrow}>
            {isGlobal ? "Global market pulse" : `Market pulse // ${marketName}`}
          </span>
          <h2>
            {isGlobal
              ? "How demand is moving across premium markets"
              : `Cum se misca piata acum in ${marketName}`}
          </h2>
          <p>
            A compact 12-month read on price direction, liquidity, inventory,
            and buyer pressure.
          </p>
        </div>
      </div>

      <div className={styles.marketBoard}>
        <div className={styles.narrativePanel}>
          {/* Header Area styled like .sectionHeader */}
          <div className={styles.panelHeader}>
            <div className={styles.titleBlock}>
              <span className={styles.eyebrow}>
                {isGlobal ? "Current benchmark" : "Benchmark curent"}
              </span>
              <h2>{formatMarketValue(latest.value, isGlobal)}</h2>
            </div>
            <p className={styles.leadText}>
              {formatGrowth(yearlyGrowth)} over the last 12 months, with demand
              still concentrated around liquid, well-connected locations.
            </p>
          </div>

          {/* Metrics styled exactly like .directoryRow */}
          <div className={styles.metricsDirectory}>
            {metrics.map(
              ({ label, value, trend, detail, Icon, isPositive = true }) => (
                <div className={styles.metricRow} key={label}>
                  {/* Left Side: Name & Detail */}
                  <div className={styles.primaryInfo}>
                    <div className={styles.nameWrapper}>
                      {Icon && (
                        <Icon className={styles.rowIcon} aria-hidden="true" />
                      )}
                      <span className={styles.itemName}>{label}</span>
                    </div>
                    {detail && (
                      <span className={styles.itemDetail}>{detail}</span>
                    )}
                  </div>

                  {/* Right Side: Data Blocks */}
                  <div className={styles.metricsInfo}>
                    <div className={styles.metricBlock}>
                      <span className={styles.metricLabel}>
                        {isGlobal ? "Value" : "Valoare"}
                      </span>
                      <span className={styles.itemValue}>{value}</span>
                    </div>

                    <div className={styles.metricBlock}>
                      <span className={styles.metricLabel}>
                        {isGlobal ? "Trend" : "Evoluție"}
                      </span>
                      <span
                        className={
                          isPositive
                            ? styles.trendPositive
                            : styles.trendNegative
                        }
                      >
                        {trend}
                      </span>
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>

        <div className={styles.chartPanel}>
          <div className={styles.chartHeader}>
            <div>
              <span>{isGlobal ? "12 month trend" : "Ultimele 12 luni"}</span>
              <h3>
                {isGlobal
                  ? "Average price movement"
                  : "Evolutia pretului mediu"}
              </h3>
            </div>
            <span className={styles.rangePill}>Jul - Jun</span>
          </div>

          <div
            className={styles.chartStage}
            onMouseLeave={() => setActiveChartTooltip(null)}
          >
            <ResponsiveLine
              data={chartData}
              margin={chartMargin}
              tooltip={() => null}
              onMouseMove={handleChartPointActivate}
              onClick={handleChartPointActivate}
              enableTouchCrosshair={true}
              xScale={{ type: "point" }}
              yScale={{
                type: "linear",
                min: "auto",
                max: "auto",
                stacked: false,
                reverse: false,
              }}
              yFormat={(value) => formatAxisValue(Number(value), isGlobal)}
              curve="monotoneX"
              colors={["var(--v2-brand-primary)"]}
              lineWidth={3}
              enableArea
              areaOpacity={0.1}
              enableGridX={false}
              gridYValues={4}
              pointSize={8}
              pointColor="var(--v2-bg-primary)"
              pointBorderWidth={2}
              pointBorderColor={{ from: "serieColor" }}
              pointLabelYOffset={-12}
              useMesh
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 0,
                tickPadding: 12,
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 12,
                tickValues: 4,
                format: (value) => formatAxisValue(Number(value), isGlobal),
              }}
              theme={{
                text: {
                  fill: "var(--v2-text-secondary)",
                  fontSize: 12,
                  fontFamily: "var(--v2-font-ui)",
                },
                axis: {
                  domain: {
                    line: {
                      stroke: "var(--v2-border-light)",
                    },
                  },
                  ticks: {
                    line: {
                      stroke: "transparent",
                    },
                    text: {
                      fill: "var(--v2-text-tertiary)",
                      fontSize: 11,
                      fontFamily: "var(--v2-font-ui)",
                    },
                  },
                },
                grid: {
                  line: {
                    stroke: "var(--v2-border-light)",
                    strokeWidth: 1,
                    strokeDasharray: "4 6",
                  },
                },
                crosshair: {
                  line: {
                    stroke: "var(--v2-brand-primary)",
                    strokeWidth: 1,
                    strokeOpacity: 0.35,
                  },
                },
                tooltip: {
                  container: {
                    fontFamily: "var(--v2-font-ui)",
                  },
                },
              }}
            />
            {activeChartTooltip && (
              <div
                className={`${styles.chartTooltip} ${
                  activeChartTooltip.isEndPoint ? styles.chartTooltipEnd : ""
                }`}
                style={
                  {
                    "--chart-tooltip-x": `${activeChartTooltip.x}px`,
                    "--chart-tooltip-y": `${activeChartTooltip.y}px`,
                  } as CSSProperties
                }
              >
                <div className={styles.tooltip}>
                  <span>{activeChartTooltip.month}</span>
                  <strong>{activeChartTooltip.value}</strong>
                  <small>
                    {formatGrowth(activeChartTooltip.growth)} month over month
                  </small>
                </div>
              </div>
            )}
          </div>

          <div
            ref={monthStripRef}
            className={`${styles.monthStrip} ${
              isMonthDragging ? styles.monthDragging : ""
            }`}
            onMouseDown={handleMonthMouseDown}
            onMouseLeave={handleMonthMouseLeave}
            onMouseUp={handleMonthMouseUp}
            onMouseMove={handleMonthMouseMove}
          >
            {trend.map((point) => (
              <div className={styles.monthCell} key={point.month}>
                <span>{point.month}</span>
                <strong>{formatGrowth(point.growth)}</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
